import { useMemo, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import PaymentCalculator, {
  CALC_DEFAULTS,
  computeMonthly,
  fmtMoney,
} from "@/components/PaymentCalculator";
import { PHOTO } from "@/lib/assets";

const CHANCES = ["1re chance au crédit", "2e chance au crédit", "3e chance au crédit"];

export default function Financing() {
  const [calc, setCalc] = useState(CALC_DEFAULTS);

  const calcInitialValues = useMemo(
    () => ({
      "Budget approximatif": fmtMoney(calc.price),
      "Mise de fonds": fmtMoney(calc.down),
    }),
    [calc.price, calc.down]
  );

  const calcPresetDetails = useMemo(
    () => ({
      "Paiement mensuel estimé": `${fmtMoney(computeMonthly(calc))} / mois`,
      "Durée du prêt simulée": `${calc.term} mois`,
      "Taux d'intérêt simulé": `${calc.rate.toFixed(2).replace(".", ",")} %`,
    }),
    [calc]
  );
  return (
    <section id="financement" data-testid="financing-section" className="py-24 md:py-32 lg:py-36">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <Chapter number="03" label="Nouveau service" />
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.12] tracking-tight">
                Des solutions de financement
                <span className="gold-text"> adaptées à votre situation.</span>
              </h2>
              <p className="mt-7 text-sm sm:text-base text-slate-300 font-light leading-relaxed">
                Grâce à notre nouveau service spécialisé en financement automobile, nous accompagnons les
                clients dans la recherche d'une solution adaptée à leur situation — un accompagnement
                personnalisé qui permet d'explorer différentes avenues selon votre dossier.
              </p>
              <div className="mt-8 relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={PHOTO.financing}
                  alt="Conseiller expliquant calmement une solution de financement à un client autour d'un bureau"
                  className="w-full h-52 object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-5 right-5 text-xs text-slate-200 font-light italic">
                  « Il existe peut-être une solution. Parlons-en. »
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <ul className="mt-8 space-y-3" data-testid="financing-chances-list">
                {CHANCES.map((c) => (
                  <li key={c} className="flex items-center gap-3 border border-white/10 bg-white/[0.03] rounded-lg px-4 py-3">
                    <BadgeCheck size={17} className="text-gold shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-slate-200">{c}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-500 font-light leading-relaxed" data-testid="financing-disclaimer">
                Chaque dossier est évalué individuellement et soumis aux conditions des prêteurs.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-8">
            <Reveal delay={0.1}>
              <PaymentCalculator value={calc} onChange={setCalc} />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="card-lux !p-8 sm:!p-10 border-gold/20">
                <h3 className="font-display text-2xl text-white mb-2">Faire une demande de financement</h3>
                <p className="text-sm text-slate-400 font-light mb-8">
                  Confidentiel et sans engagement de votre part.
                </p>
                <LeadForm
                  type="financement"
                  testid="form-financing"
                  initialValues={calcInitialValues}
                  presetDetails={calcPresetDetails}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
